import { LYW_BRAND } from '../../constants/brandPalette';
import { getSupportingMaterialPrintStyles } from './renderSupportingMaterialMarkdown';

export function getLessonPrintLogoUrl(): string {
    return `${window.location.origin}/z30-images/logo_lyw_full_400x200.png`;
}

export function escapeHtmlForPrint(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

export function getInstructionStepPrintLayoutStyles(): string {
    return `
    @media print { @page { margin: 0.75in; } }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #1f2937;
      max-width: 800px;
      margin: 0 auto;
      padding: 24px;
      background: #fff;
    }
    .print-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 20px;
      padding-bottom: 16px;
      margin-bottom: 24px;
      border-bottom: 2px solid ${LYW_BRAND.edgewater};
    }
    .print-title {
      margin: 0;
      font-size: 1.5em;
      line-height: 1.25;
      color: ${LYW_BRAND.blumineDark};
      flex: 1;
    }
    .print-logo {
      height: 52px;
      width: auto;
      flex-shrink: 0;
    }
    .print-section {
      margin-bottom: 28px;
    }
    .print-section-heading {
      margin: 0 0 14px;
      font-size: 0.8em;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: ${LYW_BRAND.blumine};
      padding-bottom: 6px;
      border-bottom: 2px solid ${LYW_BRAND.edgewater};
    }
    .step-block {
      margin-bottom: 16px;
      padding: 16px 18px;
      background: ${LYW_BRAND.confirmActionTint};
      border: 1px solid #bbf7d0;
      border-left: 4px solid ${LYW_BRAND.confirmAction};
      border-radius: 8px;
      page-break-inside: avoid;
    }
    .step-block:last-child { margin-bottom: 0; }
    .step-block-header {
      display: flex;
      align-items: baseline;
      gap: 10px;
      margin-bottom: 10px;
    }
    .step-badge {
      flex-shrink: 0;
      font-size: 0.7em;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: ${LYW_BRAND.confirmActionDark};
      background: #dcfce7;
      padding: 3px 8px;
      border-radius: 999px;
    }
    .step-title {
      margin: 0;
      font-size: 1.05em;
      font-weight: 600;
      color: #111827;
    }
    .step-instructions {
      white-space: pre-wrap;
      font-size: 0.95em;
      color: #374151;
      background: #fff;
      padding: 12px 14px;
      border-radius: 6px;
      border: 1px solid #e5e7eb;
      margin-bottom: 0;
    }
    .step-materials {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px dashed #d1d5db;
    }
    .step-materials-label {
      margin: 0 0 6px;
      font-size: 0.75em;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #6b7280;
    }
    .step-materials ul {
      margin: 0;
      padding-left: 1.25em;
      font-size: 0.9em;
      color: #4b5563;
    }
    .materials-append {
      margin-top: 8px;
    }
    .materials-append--break { page-break-before: always; }
    .material-card {
      margin-bottom: 20px;
      border-radius: 8px;
      overflow: hidden;
      page-break-inside: avoid;
      border: 1px solid #e5e7eb;
      background: #fff;
    }
    .material-card:last-child { margin-bottom: 0; }
    .material-card--break { page-break-before: always; }
    .material-card--worksheet {
      border-top: 4px solid ${LYW_BRAND.blumine};
    }
    .material-card--reading {
      border-top: 4px solid ${LYW_BRAND.monteCarlo};
    }
    .material-card--default {
      border-top: 4px solid ${LYW_BRAND.slate};
    }
    .material-card-header {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
      padding: 14px 16px;
      background: #f9fafb;
      border-bottom: 1px solid #e5e7eb;
    }
    .material-type-badge {
      flex-shrink: 0;
      font-size: 0.7em;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      padding: 4px 10px;
      border-radius: 999px;
    }
    .material-type-badge--worksheet {
      color: ${LYW_BRAND.blumine};
      background: ${LYW_BRAND.edgewater};
    }
    .material-type-badge--reading {
      color: ${LYW_BRAND.slate};
      background: #e0f2f4;
    }
    .material-type-badge--default {
      color: #4b5563;
      background: #f3f4f6;
    }
    .material-title {
      margin: 0;
      font-size: 1.1em;
      font-weight: 600;
      color: #111827;
    }
    .material-card-body {
      padding: 16px 18px;
      background: #fff;
    }
  `;
}

export function getSingleMaterialPrintLayoutStyles(): string {
    return `
    ${getInstructionStepPrintLayoutStyles()}
    .material-standalone .material-card { margin-top: 8px; }
    .print-meta {
      margin: 0 0 16px;
      font-size: 0.9em;
      color: #6b7280;
      line-height: 1.5;
    }
    ${getSupportingMaterialPrintStyles()}
  `;
}

export function getFullInstructionPrintLayoutStyles(): string {
    return `${getInstructionStepPrintLayoutStyles()}${getSupportingMaterialPrintStyles()}`;
}

export function buildPrintHeaderHtml(title: string): string {
    const logoUrl = getLessonPrintLogoUrl();
    return `
      <header class="print-header">
        <h1 class="print-title">${escapeHtmlForPrint(title)}</h1>
        <img
          src="${logoUrl}"
          alt="Learn Your Way"
          class="print-logo"
          onerror="this.style.display='none'"
        />
      </header>
    `;
}

export function buildPrintSectionHeading(label: string): string {
    return `<h2 class="print-section-heading">${escapeHtmlForPrint(label)}</h2>`;
}

export function materialCardClass(materialType?: string): string {
    switch (materialType) {
        case '10':
            return 'material-card material-card--worksheet';
        case '20':
            return 'material-card material-card--reading';
        default:
            return 'material-card material-card--default';
    }
}

export function materialTypeBadgeClass(materialType?: string): string {
    switch (materialType) {
        case '10':
            return 'material-type-badge material-type-badge--worksheet';
        case '20':
            return 'material-type-badge material-type-badge--reading';
        default:
            return 'material-type-badge material-type-badge--default';
    }
}
